import { Box, ColorOptions, Icon, Loader, ModalDialog, ModBox, Spacer, Text } from '@jfs/ui-new'
import { getErrorMessage, getLoadingState } from '@jfs/utils'
import { useNavigation, useRoute } from '@react-navigation/native'
import { FC, useCallback, useEffect, useRef, useState } from 'react'
import { useWindowDimensions as useRNWindowDimensions } from 'react-native'
import ActionsMenu from '../../comps/ActionMenu/ActionsMenu'
import EmptyView from '../../comps/EmptyView'
import { NewTable } from '../../comps/NewTable'
import { TimeOut, Toast, ToastPosition, ToastType } from '../../comps/Toast'
import { bulkRefundTitleData, MAX_TRANSACTIONS, refundTabData } from '../../data/static/data'
import { refundScreenStrings } from '../../data/static/strings'
import { useLazyApiData, useStores } from '../../data/store'
import { Routes } from '../../nav/routes'
import { ActionType, Colors, getCustomColumnWidth, getWidth, isMobile, useWindowDimensions } from '../../util/util'
import TransactionDetails from '../TransactionDetails'
import Highlights from './Highlights'
import styles from './styles'
import { RefundTypes } from './types'

const Refunds: FC = () => {
  const viewPortWidth = useWindowDimensions()

  const themes = styles(0)

  const {
    mid,
    updateSelectedTxn,
    getRefunds,
    exportFiles,
    onRefundRetry,
    getRefundScreenData,
    fetchRefund,
    isFieldsNotEmpty,
    resetFilters,
    setDownloadReportType,
    selectedDateInfo,
    updateSelectedDate,
    toastMessage,
    toastResetMessage,
    canExportRefundReport,
    pagination,
    fetchBulkSummary,
    getBulkSummaryData,
    downloadFileApi,
    getTransactionsColumn,
    isRefundedTabVisible,
    disableBulkRefund,
    bulkRefundSelectedDateInfo,
    updateBulkRefundSelectedDate,
    bulkrefundPagination,
    fetchSearchByRefundIdList,
    isSplitSettlementsEnabled,
    getSplitActionMenuData,
  } = useStores((store) => ({
    mid: store.user.selectedMid,
    updateSelectedTxn: store.transactions.updateSelectedTxn,
    getRefunds: store.transactions.getRefundsTxnsV3,
    exportFiles: store.transactions.exportFilesTxnV3,
    onRefundRetry: store.onRefundRetry,
    getRefundScreenData: store.getRefundScreenData,
    fetchRefund: store.fetchRefund,
    isFieldsNotEmpty: store.transactions.isFieldsNotEmpty,
    resetFilters: store.transactions.resetFilters,
    setDownloadReportType: store.settings.metaData.setDownloadReportType,
    selectedDateInfo: store.transactions.selectedDateInfo,
    updateSelectedDate: store.transactions.updateSelectedDateInfo,
    toastMessage: store?.reports?.getMessage(),
    toastResetMessage: store?.reports?.resetMessage,
    canExportRefundReport: store.userManagement.canExportRefundReport(),
    pagination: store.transactions.pagination,
    bulkrefundPagination: store.bulkRefund.pagination,
    getBulkSummaryData: store.bulkRefund.getBulkSummaryData,
    fetchBulkSummary: store.bulkRefund.fetchBulkSummary,
    downloadFileApi: store.bulkRefund.downloadFileApi,
    getTransactionsColumn: store.transactions.getTransactionsColumnV3,
    isRefundedTabVisible: store.userManagement.isRefundedTabVisible(),
    isBulkRefundAvailable: store.userManagement.isBulkRefundAvailable(),
    disableBulkRefund: store?.settings?.metaData?.disableBulkRefund,
    updateBulkRefundSelectedDate: store.bulkRefund.updateSelectedDateInfo,
    bulkRefundSelectedDateInfo: store.bulkRefund.selectedDateInfo,
    fetchSearchByRefundIdList: store.transactions.getSearchByRefundIdList,
    isSplitSettlementsEnabled: store.settings.metaData.isSplitSettlementsEnabled,
    getSplitActionMenuData: store.getSplitActionMenuData,
  }))

  const smallDimension = viewPortWidth < 416
  const isSmallDevice = isMobile(!smallDimension)

  const abortControllerRef = useRef<AbortController>()
  const navigate = useNavigation().navigate
  const path = useRoute().name
  const [showRefundDetails, setRefundDetails] = useState(false)
  const [trxId, setTrxId] = useState(refundScreenStrings.empty)
  const [trxDate, setTrxDate] = useState('')
  const [refundId, setRefundId] = useState<string>()

  const [fieldSettingVisible, setFieldSettingVisible] = useState(false)
  const [filterVisible, setFilterVisible] = useState(false)
  const [showMax100Info, setMax100Info] = useState(true)
  const [refundTabIndex, setRefundTabIndex] = useState(0)
  const [createSettlementVisible, setCreateSettlementVisible] = useState(false)
  const [selectedTxnForSplitOrRefund, setSelectedTxnForSplitOrRefund] = useState()

  const [fetchRefunds, fetchRefundsHandler] = useLazyApiData(getRefunds)
  const [fetchBulkSummaryList, fetchBulkSummaryHandler] = useLazyApiData(fetchBulkSummary)
  const [exportFile, expoFileHandler] = useLazyApiData(exportFiles)
  const [fetchTxnColumns, txnColumnHandler] = useLazyApiData(getTransactionsColumn)
  const [fetchSearchByRefundId, searchByHandler] = useLazyApiData(fetchSearchByRefundIdList)

  const refundApis = [fetchRefundsHandler, txnColumnHandler]
  const bulkRefundApis = [fetchBulkSummaryHandler]

  const tabData = refundTabData(isRefundedTabVisible, !disableBulkRefund)
  const getData = () => {
    if (tabData[refundTabIndex]?.title === 'Refund History') {
      return getRefundScreenData(Routes.REFUNDS, isSmallDevice)?.map((item: any) => {
        return {
          ...item,
          refundAmount: `${!isNaN(item.refundAmount) ? '₹ ' + item.refundAmount : item.refundAmount}`,
        }
      })
    } else {
      return getBulkSummaryData()
    }
  }
  const listData = getData()
  const navigateTo = () => {
    const getUrl: any = {
      [RefundTypes.transactions]: Routes.TRX_INITIATEREFUND,
      [RefundTypes.refunds]: Routes.REFUND_INITIATEREFUND,
    }
    navigate(getUrl[path] || Routes.HOME)
  }

  const fetchDetailOfPath = (tabIndex: any) => {
    abortControllerRef?.current?.abort()
    abortControllerRef.current = new AbortController()
    path && setRefundDetails(false)
    fetchRefund(
      fetchRefunds,
      selectedDateInfo,
      fetchBulkSummaryList,
      tabData[tabIndex]?.title,
      fetchTxnColumns,
      abortControllerRef?.current,
    )
  }

  const fetchDetailsOfPath = useCallback(fetchDetailOfPath, [fetchRefunds])

  useEffect(() => {
    resetFilters()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path])

  useEffect(() => {
    fetchDetailsOfPath(refundTabIndex)
  }, [path, mid, fetchDetailsOfPath, isFieldsNotEmpty, refundTabIndex])

  const handleRetry = (): void => {
    abortControllerRef?.current?.abort()
    abortControllerRef.current = new AbortController()
    onRefundRetry(fetchRefunds, abortControllerRef.current)
  }

  const onPress = (item: any) => {
    if (refundTabIndex === 0) {
      setRefundId(item?.transactionId)
      setTrxDate(item.transactionDateTime)
      setTrxId(item.id)
      setRefundDetails(true)
    }
  }

  const closeTrxDetailsView = () => {
    setTrxId('')
    setRefundId(undefined)
    setRefundDetails(false)
  }

  const initiateRefund = (id: string) => {
    updateSelectedTxn(id)
    navigateTo()
    closeTrxDetailsView()
  }

  const renderRefundDetails = (): React.ReactElement => {
    return (
      <TransactionDetails
        transactionId={trxId}
        transactionDate={trxDate}
        visible={showRefundDetails}
        closeTrxDetailsView={closeTrxDetailsView}
        initiateRefund={initiateRefund}
        path={Routes.REFUNDS}
        refundId={refundId}
      />
    )
  }

  const loadingState = getLoadingState(refundTabIndex === 0 ? refundApis : bulkRefundApis)
  const errorState = getErrorMessage(refundTabIndex === 0 ? refundApis : bulkRefundApis)
  const emptyState = listData.length > 0 && !loadingState

  const getEmptyMessage = (): string => {
    return listData.length === 0 ? refundScreenStrings.noRecords : refundScreenStrings.empty
  }

  const closeModal = () => {
    setRefundDetails(false)
  }

  const scrollViewStyle = () => {
    if (listData.length === 0) {
      return themes[Routes.REFUNDS as RefundTypes].emptyContainer
    }
    return themes[Routes.REFUNDS as RefundTypes].tableContainer(loadingState)
  }
  const scrollStyle = scrollViewStyle()

  //To fix transactionType going as transactions instead of REFUND
  const handleExportFile = (fileType: string, pathFrom: string, dateInfo: any) => {
    exportFile(fileType, 'REFUND', dateInfo)
  }

  const handleToastAction = () => {
    setDownloadReportType('Refund')
    toastResetMessage()
    navigate(Routes.DOWNLOADS)
  }
  const handleToastDismiss = () => {
    toastResetMessage()
  }

  const updateTabIndex = (val: any) => {
    setRefundTabIndex(val)
    // console.log('refetch trans val', val)
    if (val === 0) {
      fetchDetailsOfPath(refundTabIndex) // TODO: seems the refund transaction not reflecting instantly verify once. pull latest data from server
    }
  }

  const onFileDownloadClick = (actionType: ActionType, id: any) => {
    if (tabData[refundTabIndex]?.title === 'Bulk Refund History') {
      console.log(actionType)
      const clickedItem = listData.find((element: any) => element.id === id)
      if (clickedItem) {
        downloadFileApi(clickedItem?.id, clickedItem?.fileName)
      }
    }
  }

  const handleRefundsSearchBy = (searchByTxt: string) => {
    if (searchByTxt?.length > 0) {
      fetchSearchByRefundId(mid, searchByTxt?.toString()?.trim(), 'REFUND')
    } else if (searchByTxt?.length === 0) {
      path && setRefundDetails(false)
      fetchRefund(fetchRefunds, selectedDateInfo, fetchBulkSummaryList, tabData[refundTabIndex]?.title, fetchTxnColumns)
    }
  }

  const onRefresh = () => {
    fetchDetailsOfPath(refundTabIndex)
  }

  const onCreateSplitClick = (item) => {
    setSelectedTxnForSplitOrRefund(item)
    setCreateSettlementVisible(true)
  }

  const isFullScreenScrollable = useRNWindowDimensions()?.height < 543
  const refunds = refundTabIndex === 0 ? listData.slice(1) : listData
  return (
    <Box flex={1} overflowY={fieldSettingVisible || filterVisible || isFullScreenScrollable ? 'auto' : 'hidden'}>
      <Box {...themes.maincontainer}>
        <Toast
          message={toastMessage}
          hide={toastMessage.message === ''}
          position={ToastPosition.TOP_POSITIVE_ACTION_VIEW}
          type={ToastType.POSITIVE_ACTION_VIEW}
          onDismiss={handleToastDismiss}
          onAction={handleToastAction}
          actionLabel={'Go To Downloads'}
          timeOut={TimeOut.FIVE_SECONDS}
        />
        <ModalDialog children={renderRefundDetails()} show={showRefundDetails} type={'Right'} onDismiss={closeModal} />
        <Highlights canViewHighlightsData={true} updateTabIndex={updateTabIndex} onDoneUpload={onRefresh} />
        <Spacer direction={'vertical'} size={16} />
        <Box {...themes[Routes.REFUNDS as RefundTypes].container} flex={emptyState ? undefined : 1}>
          <Box {...themes.stickyHeader}>
            <ActionsMenu
              path={tabData[refundTabIndex]?.title === 'Refund History' ? 'transactions' : 'bulkRefund'} //Should be transactions as its used to map the pagination in ActionsMenu
              handleExport={handleExportFile}
              hideFieldSettings
              fetchDetailOfPath={() => fetchDetailOfPath(refundTabIndex)}
              fieldSettingVisible={fieldSettingVisible}
              setFieldSettingVisible={setFieldSettingVisible}
              filterVisible={filterVisible}
              setFilterVisible={setFilterVisible}
              selectedDateInfo={
                tabData[refundTabIndex]?.title === 'Refund History' ? selectedDateInfo : bulkRefundSelectedDateInfo
              }
              updateSelectedDate={
                tabData[refundTabIndex]?.title === 'Refund History' ? updateSelectedDate : updateBulkRefundSelectedDate
              }
              hideExportButton={!canExportRefundReport}
              maxRange={31}
              refundTabBarData={tabData}
              refundTabIndex={refundTabIndex}
              setRefundTabIndex={updateTabIndex}
              fetchTxnColumns={fetchTxnColumns}
              showExportOnRight={true}
              screenName={'Refunds'}
              handleRefundsSearchBy={handleRefundsSearchBy}
              onDownloadRefreshList={onRefresh}
            />
          </Box>
          <ModBox
            show={
              tabData[refundTabIndex]?.title === 'Refund History'
                ? pagination?.totalElements === MAX_TRANSACTIONS
                : bulkrefundPagination?.totalElements === MAX_TRANSACTIONS && showMax100Info
            }
            flexDirection={'row'}
            align={'center'}
            maxHeight={24}
            borderWidth={1}
            ph={8}
            pv={2}
            borderRadius={4}
            borderColor={Colors.INFO_BORDER as ColorOptions}
            bg={Colors.INFO_BG as ColorOptions}>
            <Box flexGrow={1} flexDirection={'row'} align={'center'} f={1}>
              <Icon id={'INFO'} color={'BRAND_PRIMARY_ORANGE'} size={16} />
              <Spacer direction={'horizontal'} size={8} />
              <Text
                title={`Showing only upto ${MAX_TRANSACTIONS} records here. To check more records, you can 'Export' the records. The reports will be downloaded in the 'Download' section of the menu.`}
                fontSize={12}
                fontFamily={'jiotype-medium'}
                color={'BLACK_21'}
                numberOfLines={1}
              />
            </Box>
            <Box underlayColor={'TRANSPARENT'} testID="close" onPress={() => setMax100Info(false)}>
              <Icon id={'CLOSE'} color={'BLACK_21'} size={14} />
            </Box>
          </ModBox>
          <EmptyView
            onRetry={handleRetry}
            isLoading={loadingState}
            error={errorState}
            emptyMessage={getEmptyMessage()}
            loadingMessage={refundScreenStrings.loading}
            hide={emptyState}
            loaderColor={'BLUE'}
          />
          <ModBox show={expoFileHandler.state === 'pending'}>
            <Loader color="BLUE" />
          </ModBox>
          <Spacer size={5} direction="vertical" />
          <Box
            {...scrollStyle}
            nativeID={'scroll-horizontal-Table'}
            f={1}
            minHeight={isFullScreenScrollable && refunds?.length > 0 ? 120 : undefined}>
            <NewTable
              data={refundTabIndex === 0 ? listData.slice(1) : listData}
              onRowClick={onPress}
              rowHeight={48}
              headerHeight={64}
              onAction={onFileDownloadClick}
              headerRowKeys={refundTabIndex === 0 ? listData[0] : bulkRefundTitleData}
              customWidthFunc={getCustomColumnWidth}
              hide={filterVisible || fieldSettingVisible || !(!loadingState && !errorState && !getEmptyMessage())}
              sortBy="transactionDateTime"
              tableWidth={getWidth(viewPortWidth > 1200 ? viewPortWidth - 120 : viewPortWidth + 360, listData[0])}
              isSplitSettlementsEnabled={isSplitSettlementsEnabled}
              splitActionMenuData={(rowData) =>
                getSplitActionMenuData(onCreateSplitClick, () => initiateRefund(rowData?.transactionId), rowData, path)
              }
            />
          </Box>
        </Box>
      </Box>
    </Box>
  )
}

export default Refunds
